import { useCallback } from "react";

import { useSelector } from "../redux/hooks";
/**
 * Mapping `originalText->nonMaritimeText`. for replacing the original text with the non-maritime text.
 */
const nonMaritimeTexts: Record<string, string> = {
  "Yacht Verified": "Verified",
  Onboard: "Active",
  "Rotational Leave": "Rotation",
};
type MaritimeSpecialTypes = "reportDropdown";
const maritimeSpecialKeys: Record<MaritimeSpecialTypes, string[]> = {
  reportDropdown: ["1", "3", "4"],
};
/**
 * A hook to retrieve non-maritime names based on the provided original name. So if user is nonMaritime in couple places original text needs replacing.
 *
 * @returns A callback function that takes an original name and returns its non-maritime equivalent if available.
 */
const useNonMaritime = () => {
  const { nonMaritime } = useSelector(state => state.permsSlice);
  /**
   * A function to replace maritime text with non-maritime text.
   *
   * @param originalText - The original text to be replaced.
   * @returns The replaced text if nonMaritime is true, otherwise the original text.
   */
  const replaceMaritimeText = useCallback(
    (originalText: string | undefined) => {
      if (!originalText) return originalText;
      if (nonMaritime) {
        const matchedNonMaritimeText = Object.keys(nonMaritimeTexts).find(
          text => originalText.includes(text),
        );
        if (matchedNonMaritimeText) {
          return originalText.replace(
            matchedNonMaritimeText,
            nonMaritimeTexts[matchedNonMaritimeText],
          );
        }
        return originalText;
      }
      return originalText;
    },
    [nonMaritime],
  );

  /**
   * A function to filter out maritime special keys from the data array.
   *
   * @param data - The data array to be filtered.
   * @param keyNameToCompare - The key name to compare against the maritime special keys.
   * @param type - Type of the data that should be compared
   * @returns The filtered data array without maritime special keys.
   */
  const replaceMaritimeSpecialKeys = useCallback(
    (
      data: any[],
      keyNameToCompare: string,
      type: keyof typeof maritimeSpecialKeys,
    ) => {
      if (!nonMaritime) return data;
      return data.filter(
        item => !maritimeSpecialKeys[type]?.includes(item[keyNameToCompare]),
      );
    },
    [nonMaritime],
  );
  return { replaceMaritimeText, replaceMaritimeSpecialKeys, nonMaritime };
};
export default useNonMaritime;
